import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import DateInput from '@/components/ui/DateInput';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Search,
  Calendar,
  Receipt,
  Users,
  CheckCircle,
  XCircle,
  ChevronRight,
  ReceiptText,
  ArrowLeft,
  Pencil,
  Home,
  Layers,
  UserCheck,
  ZoomIn,
  Copy,
  Check,
} from 'lucide-react';
import { Expense, ExpenseParticipant, User, ViewType } from '../types';
import { ImageLightbox } from '@/components/ui/ImageLightbox';

function getDefaultStartDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 13); // last 14 days including today
  return d.toISOString().split('T')[0];
}

export default function TransactionHistory({
  store,
  setView,
  initialQuery = '',
}: {
  store: any;
  setView: (v: ViewType) => void;
  initialQuery?: string;
}) {
  const { expenses, currentUser, users, setEditingExpense } = store;

  const [inputValue, setInputValue] = useState(initialQuery);
  const [keyword, setKeyword] = useState(initialQuery);
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Inline detail state
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [selectedRole, setSelectedRole] = useState<'creator' | 'participant'>('creator');
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleShare = () => {
    if (typeof window === 'undefined') return;
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Auto-open expense from URL on initial load (e.g. /history/:id)
  const didAutoOpen = useRef(false);
  useEffect(() => {
    if (didAutoOpen.current || !expenses.length) return;
    if (typeof window === 'undefined') return;
    const match = window.location.pathname.match(/^\/history\/(.+)/);
    if (!match) { didAutoOpen.current = true; return; }
    const id = match[1];
    const found = expenses.find((e: Expense) => String(e.id) === id);
    if (found) {
      const role = found.payerId === currentUser?.id ? 'creator' : 'participant';
      setSelectedExpense(found);
      setSelectedRole(role);
      didAutoOpen.current = true;
    }
  }, [expenses]);

  // Sync URL when detail opens/closes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (selectedExpense) {
      window.history.replaceState(null, '', `/history/${selectedExpense.id}`);
    } else {
      window.history.replaceState(null, '', '/history');
    }
  }, [selectedExpense]);

  // Sync when initialQuery changes (e.g. user searches from topbar again)
  useEffect(() => {
    setInputValue(initialQuery);
    setKeyword(initialQuery);
    // New search from topbar clears detail panel
    setSelectedExpense(null);
  }, [initialQuery]);

  const handleSearch = () => {
    setKeyword(inputValue);
    setSelectedExpense(null);
  };

  const filteredExpenses: Expense[] = expenses.filter((exp: Expense) => {
    const isRelated =
      exp.payerId === currentUser?.id ||
      exp.participants.some((p) => p.userId === currentUser?.id);
    if (!isRelated) return false;

    if (keyword) {
      const q = keyword.toLowerCase();
      if (!exp.title.toLowerCase().includes(q)) return false;
    }

    const expDate = exp.expenseDate.split('T')[0];
    if (startDate && expDate < startDate) return false;
    if (endDate && expDate > endDate) return false;

    return true;
  });

  // Merged list sorted by date desc
  type RichExpense = { expense: Expense; role: 'creator' | 'participant' };
  const mergedList: RichExpense[] = filteredExpenses
    .map((exp) => ({
      expense: exp,
      role: exp.payerId === currentUser?.id ? 'creator' : 'participant',
    } as RichExpense))
    .sort((a, b) =>
      new Date(b.expense.expenseDate).getTime() - new Date(a.expense.expenseDate).getTime()
    );

  const getUserName = (id: string) =>
    users.find((u: User) => u.id === id)?.nickname || '未知';

  const getUserAvatar = (id: string) =>
    users.find((u: User) => u.id === id)?.avatarUrl;

  // ── Detail Panel ─────────────────────────────────────────────────────────
  if (selectedExpense) {
    const exp = selectedExpense;
    const role = selectedRole;
    const isCreator = role === 'creator';
    const myParticipation = exp.participants.find((p) => p.userId === currentUser?.id);
    const payer = users.find((u: User) => u.id === exp.payerId);

    return (
      <div className="space-y-6">
        {/* Inline breadcrumb for detail level */}
        <nav className="flex items-center gap-2 text-sm font-medium text-muted-foreground" aria-label="detail breadcrumb">
          <Home size={14} />
          <button
            onClick={() => setView('dashboard')}
            className="hover:text-primary transition-colors"
          >
            首頁
          </button>
          <ChevronRight size={14} className="opacity-40" />
          <button
            onClick={() => setSelectedExpense(null)}
            className="hover:text-primary transition-colors"
          >
            查詢交易歷史
          </button>
          <ChevronRight size={14} className="opacity-40" />
          <span className="text-foreground font-bold">支出詳情</span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedExpense(null)}
              className="rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl md:text-4xl font-black tracking-tight">{exp.title}</h1>
                {exp.imageUrl && <ReceiptText size={16} className="text-muted-foreground mt-1 shrink-0" />}
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  className={`text-[10px] font-black px-2 py-0.5 rounded-full border-0 ${
                    isCreator
                      ? 'bg-primary/15 text-primary'
                      : 'bg-blue-500/15 text-blue-400'
                  }`}
                >
                  {isCreator ? '我建立' : '分帳對象'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 h-10 px-4 rounded-xl font-black text-xs bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
            >
              {copied ? <Check size={14} className="text-secondary" /> : <Copy size={14} />}
              {copied ? '已複製' : '分享'}
            </button>
            {isCreator && (
              <Button
                onClick={() => { setEditingExpense(exp); setView('edit-expense'); }}
                className="rounded-xl font-black h-10 px-4 bg-primary/10 text-primary hover:bg-primary/20 border-0 shrink-0"
                variant="ghost"
              >
                <Pencil size={16} className="mr-2" />
                修改
              </Button>
            )}
          </div>
        </div>

        {/* Image */}
        {exp.imageUrl && (
          <div
            className="relative rounded-2xl overflow-hidden cursor-zoom-in group border-2 border-border/40 hover:border-primary/50 transition-colors min-h-[180px] flex items-center justify-center bg-black/10"
            onClick={() => setZoomedImage(exp.imageUrl!)}
          >
            <img src={exp.imageUrl} alt={exp.title} className="w-full h-auto min-h-[100px] max-h-[300px] object-contain bg-black/20" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="p-3 bg-white/20 backdrop-blur-md rounded-full text-white">
                <ZoomIn size={24} />
              </div>
            </div>
          </div>
        )}

        {/* Basic Info */}
        <Card className="bg-muted/30 border-border/40 rounded-2xl p-5 space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs font-black text-muted-foreground uppercase tracking-[0.15em] mb-1">日期</p>
              <p className="font-bold flex items-center gap-1.5">
                <Calendar size={14} className="text-muted-foreground" />
                {new Date(exp.expenseDate).toLocaleDateString('zh-TW')}
              </p>
            </div>
            <div>
              <p className="text-xs font-black text-muted-foreground uppercase tracking-[0.15em] mb-1">付款人</p>
              <p className="font-bold flex items-center gap-1.5">
                <Avatar className="w-5 h-5 rounded-md overflow-hidden">
                  <AvatarImage src={getUserAvatar(exp.payerId)} className="object-cover" />
                  <AvatarFallback className="text-[8px]">{payer?.nickname?.[0]}</AvatarFallback>
                </Avatar>
                {getUserName(exp.payerId)}
              </p>
            </div>
            <div>
              <p className="text-xs font-black text-muted-foreground uppercase tracking-[0.15em] mb-1">總金額</p>
              <p className="text-xl font-black text-primary">${exp.amount.toLocaleString()}</p>
            </div>
            {myParticipation && (
              <div>
                <p className="text-xs font-black text-muted-foreground uppercase tracking-[0.15em] mb-1">我的應付</p>
                <div className={`flex items-center gap-1.5 font-bold ${myParticipation.isPaid ? 'text-secondary' : 'text-destructive'}`}>
                  {myParticipation.isPaid ? <CheckCircle size={14} /> : <XCircle size={14} />}
                  ${myParticipation.amountDue.toLocaleString()} {myParticipation.isPaid ? '（已付）' : '（未付）'}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Participants */}
        <div>
          <h3 className="text-xs font-black text-muted-foreground uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
            <Layers size={12} /> 分帳參與人員 ({exp.participants.length})
          </h3>
          <div className="space-y-2">
            {exp.participants.map((p: ExpenseParticipant) => {
              const u = users.find((user: User) => user.id === p.userId);
              const isPayer = exp.payerId === p.userId;
              return (
                <div
                  key={p.userId}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${p.isPaid ? 'border-secondary/30 bg-secondary/5' : 'border-border/20 bg-background/40'}`}
                >
                  <Avatar className={`w-9 h-9 rounded-xl overflow-hidden border-2 ${p.isPaid ? 'border-secondary/40' : 'border-border/30'}`}>
                    <AvatarImage src={getUserAvatar(p.userId)} className="object-cover" />
                    <AvatarFallback className="text-xs font-bold">
                      {p.isPaid ? <UserCheck size={14} /> : u?.nickname?.[0] || '？'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">
                      {getUserName(p.userId)}
                      {isPayer && <span className="ml-2 text-[10px] text-muted-foreground font-medium">（付款人）</span>}
                    </p>
                    <p className="text-xs text-muted-foreground font-medium">應付 ${p.amountDue.toLocaleString()}</p>
                  </div>
                  {p.isPaid ? (
                    <Badge className="bg-secondary/20 text-secondary border-0 text-[10px] font-black">已付</Badge>
                  ) : (
                    <Badge className="bg-destructive/10 text-destructive border-0 text-[10px] font-black">未付</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Back button (footer) */}
        <div className="pt-2">
          <Button
            variant="ghost"
            onClick={() => setSelectedExpense(null)}
            className="rounded-xl font-black text-muted-foreground hover:text-primary hover:bg-primary/10 h-12 px-6"
          >
            <ArrowLeft size={16} className="mr-2" />
            返回查詢結果
          </Button>
        </div>

        {/* Zoomed image lightbox */}
        {zoomedImage && (
          <ImageLightbox src={zoomedImage} onClose={() => setZoomedImage(null)} />
        )}
      </div>
    );
  }

  // ── List View ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">
      {/* Title */}
      <div>
        <h1 className="text-3xl md:text-5xl font-black tracking-tight text-primary mb-2">查詢交易歷史</h1>
        <p className="text-muted-foreground font-medium">搜尋與您相關的所有支出紀錄。</p>
      </div>

      {/* Search Controls */}
      <Card className="bg-muted/30 border-border/50 rounded-2xl md:rounded-[2.5rem] p-5 md:p-8 shadow-wash">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
          {/* Keyword */}
          <div className="space-y-2">
            <label className="text-xs font-black text-muted-foreground uppercase tracking-[0.2em]">項目名稱（模糊查詢）</label>
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-background border-none rounded-xl h-12 font-medium"
                placeholder="輸入支出名稱..."
              />
            </div>
          </div>

          {/* Start date */}
          <div className="space-y-2">
            <label className="text-xs font-black text-muted-foreground uppercase tracking-[0.2em]">開始日期</label>
            <DateInput
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              wrapperClassName="w-full md:w-40"
              className="bg-background border-none rounded-l-xl h-12 font-bold"
            />
          </div>

          {/* End date */}
          <div className="space-y-2">
            <label className="text-xs font-black text-muted-foreground uppercase tracking-[0.2em]">結束日期</label>
            <DateInput
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              wrapperClassName="w-full md:w-40"
              className="bg-background border-none rounded-l-xl h-12 font-bold"
            />
          </div>

          {/* Search button */}
          <Button
            onClick={handleSearch}
            className="h-12 rounded-xl font-black px-6 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Search size={16} className="mr-2" />
            查詢
          </Button>
        </div>
      </Card>

      {/* Summary badges */}
      {(keyword || startDate || endDate) && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-bold text-muted-foreground">查詢結果：</span>
          <Badge variant="outline" className="border-primary/30 text-primary font-bold">
            共 {mergedList.length} 筆
          </Badge>
          {keyword && (
            <Badge variant="outline" className="border-border font-medium text-[10px]">
              關鍵字：{keyword}
            </Badge>
          )}
          <Badge variant="outline" className="border-border font-medium text-[10px]">
            {startDate} ～ {endDate}
          </Badge>
        </div>
      )}

      {/* Merged list */}
      <div className="space-y-2">
        {mergedList.length > 0 ? (
          mergedList.map(({ expense: exp, role }) => {
            const myParticipation = exp.participants.find((p) => p.userId === currentUser?.id);
            const payer = users.find((u: User) => u.id === exp.payerId);
            const isPaid = myParticipation?.isPaid ?? true;
            const isCreator = role === 'creator';

            return (
              <div
                key={exp.id}
                className="flex items-center gap-4 p-4 bg-background/50 rounded-2xl border border-border/20 hover:border-primary/30 hover:translate-x-0.5 transition-all group cursor-pointer"
                onClick={() => { setSelectedExpense(exp); setSelectedRole(role); }}
              >
                {/* Role icon */}
                <div
                  className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center ${
                    isCreator ? 'bg-primary/10 text-primary' : 'bg-blue-500/10 text-blue-400'
                  }`}
                >
                  {isCreator ? <Receipt size={20} /> : <Users size={20} />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-bold truncate">{exp.title}</p>
                    {exp.imageUrl && (
                      <ReceiptText size={12} className="text-muted-foreground shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-[10px] text-muted-foreground font-medium">
                    <span className="flex items-center gap-1">
                      <Calendar size={10} />
                      {new Date(exp.expenseDate).toLocaleDateString('zh-TW')}
                    </span>
                    {/* Role badge */}
                    <span
                      className={`font-black px-1.5 py-0.5 rounded-full ${
                        isCreator
                          ? 'bg-primary/10 text-primary'
                          : 'bg-blue-500/10 text-blue-400'
                      }`}
                    >
                      {isCreator ? '我建立' : '分帳對象'}
                    </span>
                    {!isCreator && (
                      <span className="flex items-center gap-1">
                        <Avatar className="w-4 h-4 rounded-md overflow-hidden">
                          <AvatarImage src={getUserAvatar(exp.payerId)} className="object-cover" />
                          <AvatarFallback className="text-[8px]">{payer?.nickname?.[0]}</AvatarFallback>
                        </Avatar>
                        {getUserName(exp.payerId)} 付款
                      </span>
                    )}
                    {isCreator && (
                      <span>{exp.participants.length} 位參與者</span>
                    )}
                  </div>
                </div>

                {/* Amount + status */}
                <div className="text-right shrink-0">
                  <div className="text-base font-black text-foreground">
                    ${exp.amount.toLocaleString()}
                  </div>
                  {!isCreator && myParticipation && (
                    <div className={`text-[10px] font-bold flex items-center gap-1 justify-end mt-0.5 ${isPaid ? 'text-secondary' : 'text-destructive'}`}>
                      {isPaid ? <CheckCircle size={10} /> : <XCircle size={10} />}
                      {isPaid ? '已支付' : `應付 $${myParticipation.amountDue.toLocaleString()}`}
                    </div>
                  )}
                  {isCreator && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {exp.participants.filter((p) => p.isPaid).length}/{exp.participants.length} 已付
                    </div>
                  )}
                </div>

                <ChevronRight size={16} className="text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
              </div>
            );
          })
        ) : (
          <div className="p-16 text-center text-muted-foreground bg-muted/20 rounded-2xl border-2 border-dashed border-border/40 text-sm font-medium">
            此期間無相關紀錄
          </div>
        )}
      </div>
    </div>
  );
}
